import { API_BASE } from './config'

export const AUTH_TOKEN_KEY = 'gutstring_access_token'
export const AUTH_CHANGE_EVENT = 'gutstring-auth-change'
export const SESSION_EXPIRED_FLAG = 'gutstring_session_expired'
export const SESSION_EXPIRED_MESSAGE = 'Your session expired. Please log in again.'

export type LoginLocationState = {
  message?: string
}

function markSessionExpired() {
  sessionStorage.setItem(SESSION_EXPIRED_FLAG, '1')
}

export function consumeSessionExpiredFlag(): boolean {
  if (sessionStorage.getItem(SESSION_EXPIRED_FLAG) !== '1') return false
  sessionStorage.removeItem(SESSION_EXPIRED_FLAG)
  return true
}

function notifyAuthChange() {
  window.dispatchEvent(new Event(AUTH_CHANGE_EVENT))
}

function isJwtExpired(token: string): boolean {
  try {
    const payloadPart = token.split('.')[1]
    if (!payloadPart) return true
    const base64 = payloadPart.replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(base64)) as { exp?: number }
    if (typeof payload.exp !== 'number') return false
    return payload.exp * 1000 <= Date.now()
  } catch {
    return false
  }
}

function readStoredToken(): string | null {
  const stored = localStorage.getItem(AUTH_TOKEN_KEY)
  if (stored) return stored.trim() || null
  const legacy = sessionStorage.getItem(AUTH_TOKEN_KEY)
  if (!legacy) return null
  const token = legacy.trim()
  if (!token) return null
  localStorage.setItem(AUTH_TOKEN_KEY, token)
  sessionStorage.removeItem(AUTH_TOKEN_KEY)
  return token
}

export interface RegisterPayload {
  email: string
  password: string
  username: string
  displayName?: string
}

export interface LoginPayload {
  login: string
  password: string
}

export interface UserStats {
  followers: number
  following: number
  strings: number
}

export interface EmailNotificationSettings {
  enabled: boolean
  lastDigestSentAt?: string | null
}

export interface UserSettings {
  profileVisibility?: string
  showFollowerCount?: boolean
  emailNotifications?: EmailNotificationSettings
}

export interface UserPublic {
  userId: string
  username: string
  needsUsername?: boolean
  displayName: string
  bio: string
  profileImageUrl: string | null
  coverImageUrl: string | null
  badges: string[]
  stats?: UserStats
  settings?: UserSettings
  createdAt: string
  updatedAt: string
}

export interface AuthResponse {
  user: UserPublic
  access_token: string
  token_type: string
}

/** Full-page redirect to API → Google → back to /auth/callback with JWT. */
export function startGoogleSignIn(): void {
  window.location.href = `${API_BASE}/auth/google/start`
}

export async function parseApiError(
  res: Response,
  options?: { authenticated?: boolean; clearTokenOn401?: boolean },
): Promise<string> {
  if (res.status === 401) {
    if (options?.authenticated) {
      markSessionExpired()
      clearAuthToken()
      return SESSION_EXPIRED_MESSAGE
    }
    if (options?.clearTokenOn401 && getAuthToken()) {
      clearAuthToken()
    }
  }
  try {
    const data = await res.json()
    if (typeof data.detail === 'string') return data.detail
    if (Array.isArray(data.detail)) {
      return data.detail
        .map((item: { msg?: string }) => item.msg)
        .filter(Boolean)
        .join(' ')
    }
  } catch {
    /* ignore */
  }
  if (res.status === 0 || res.status >= 500) {
    return 'Could not reach the server. Please try again later.'
  }
  return 'Something went wrong. Please try again.'
}

export async function checkUsernameAvailable(username: string): Promise<boolean> {
  const res = await fetch(
    `${API_BASE}/auth/username-available?username=${encodeURIComponent(username)}`,
  )
  if (!res.ok) throw new Error(await parseApiError(res))
  const data: { available: boolean } = await res.json()
  return data.available
}

export async function registerUser(
  payload: RegisterPayload,
): Promise<AuthResponse> {
  const res = await fetch(`${API_BASE}/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email: payload.email,
      password: payload.password,
      username: payload.username,
      displayName: payload.displayName?.trim() || undefined,
    }),
  })
  if (!res.ok) throw new Error(await parseApiError(res))
  return res.json()
}

export async function loginUser(payload: LoginPayload): Promise<AuthResponse> {
  const res = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await parseApiError(res))
  return res.json()
}

export function saveAuthToken(token: string) {
  const trimmed = token.trim()
  if (!trimmed) return
  localStorage.setItem(AUTH_TOKEN_KEY, trimmed)
  sessionStorage.removeItem(AUTH_TOKEN_KEY)
  notifyAuthChange()
}

/** JWT from register/login; survives refresh and browser restarts (localStorage). */
export function getAuthToken(): string | null {
  const token = readStoredToken()
  if (!token) return null
  if (isJwtExpired(token)) {
    markSessionExpired()
    clearAuthToken()
    return null
  }
  return token
}

export function clearAuthToken() {
  const hadToken = Boolean(readStoredToken())
  localStorage.removeItem(AUTH_TOKEN_KEY)
  sessionStorage.removeItem(AUTH_TOKEN_KEY)
  if (hadToken) notifyAuthChange()
}

export function isLoggedIn(): boolean {
  return Boolean(getAuthToken())
}

export function getRequiredAuthToken(fallbackMessage: string): string {
  const token = getAuthToken()
  if (!token) {
    if (consumeSessionExpiredFlag()) {
      throw new Error(SESSION_EXPIRED_MESSAGE)
    }
    throw new Error(fallbackMessage)
  }
  return token
}

export interface ChangePasswordPayload {
  currentPassword: string
  newPassword: string
}

export async function changePassword(
  payload: ChangePasswordPayload,
): Promise<void> {
  const token = getRequiredAuthToken('Log in to change your password.')
  const res = await fetch(`${API_BASE}/auth/change-password`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload),
  })
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
}

export interface CompleteGoogleProfilePayload {
  username: string
  displayName?: string
}

export async function completeGoogleProfile(
  payload: CompleteGoogleProfilePayload,
): Promise<AuthResponse> {
  const token = getRequiredAuthToken('Sign in again to choose your username.')
  const res = await fetch(`${API_BASE}/auth/google/complete-profile`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      username: payload.username,
      displayName: payload.displayName?.trim() || undefined,
    }),
  })
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}

export async function fetchMe(): Promise<UserPublic> {
  const token = getRequiredAuthToken('Log in to view your profile.')
  const res = await fetch(`${API_BASE}/auth/me`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error(await parseApiError(res, { authenticated: true }))
  return res.json()
}
